/**
 * จัดเตรียมวัตถุดิบ (Staging) — `/v1/production-orders/{id}/staging` (D-027, D-062, D-063)
 *
 * Staging จอง (reserve) วัตถุดิบให้ operation หนึ่งและไม่เคลื่อน stock ตัวเลขทั้งสามเป็น string แยกกัน (staged/reserved/consumed)
 */

import {
  callJson,
  newIdempotencyKey,
  type CallResult,
  type StockFailure,
  type StockRequestOptions,
} from './stock';

export interface StagingRow {
  readonly staging_id: string;
  readonly operation_seq: number;
  readonly product_code: string;
  readonly lot_id: string;
  readonly source_location_code: string;
  readonly staging_location_code: string;
  readonly staged_qty: string;
  readonly reserved_qty: string;
  readonly consumed_qty: string;
  readonly status: string;
}

export interface StagingTransferInput {
  readonly plant_code: string;
  readonly operation_seq: number;
  readonly product_code: string;
  readonly lot_id: string;
  readonly source_location_code: string;
  readonly staging_location_code: string;
  readonly quantity: string;
}

export type StagingListResult =
  | { readonly status: 'ready'; readonly items: readonly StagingRow[] }
  | StockFailure;
export type StagingTransferResult =
  | { readonly status: 'ready'; readonly row: StagingRow }
  | StockFailure;

export function stagingPath(orderId: string): string {
  return `/v1/production-orders/${encodeURIComponent(orderId)}/staging`;
}

export function stagingTransfersPath(orderId: string): string {
  return `/v1/production-orders/${encodeURIComponent(orderId)}/staging-transfers`;
}

function s(value: unknown): string | null {
  return typeof value === 'string' && value !== '' ? value : null;
}

export function parseStagingRow(raw: unknown): StagingRow | null {
  if (typeof raw !== 'object' || raw === null) {
    return null;
  }
  const r = raw as Record<string, unknown>;
  const fields = [
    'staging_id',
    'product_code',
    'lot_id',
    'source_location_code',
    'staging_location_code',
    'staged_qty',
    'reserved_qty',
    'consumed_qty',
    'status',
  ] as const;
  const out: Record<string, string | number> = {};
  for (const field of fields) {
    const value = s(r[field]);
    if (value === null) {
      return null;
    }
    out[field] = value;
  }
  if (typeof r.operation_seq !== 'number') {
    return null;
  }
  out.operation_seq = r.operation_seq;
  return out as unknown as StagingRow;
}

function fromCall(result: CallResult): { readonly payload: unknown } | StockFailure {
  return result.status === 'ok' ? { payload: result.payload } : result;
}

export async function fetchStaging(
  orderId: string,
  options: StockRequestOptions = {},
): Promise<StagingListResult> {
  const result = fromCall(await callJson('GET', `${stagingPath(orderId)}?limit=200`, undefined, {}, options));
  if (!('payload' in result)) {
    return result;
  }
  const body = result.payload as { items?: unknown } | null;
  if (typeof body !== 'object' || body === null || !Array.isArray(body.items)) {
    return { status: 'invalid-response' };
  }
  const items: StagingRow[] = [];
  for (const entry of body.items) {
    const row = parseStagingRow(entry);
    if (row === null) {
      return { status: 'invalid-response' };
    }
    items.push(row);
  }
  return { status: 'ready', items };
}

/** `POST …/staging-transfers` — ต้องมี `Idempotency-Key` (หนึ่งการกด หนึ่งคีย์) */
export async function createStagingTransfer(
  orderId: string,
  input: StagingTransferInput,
  idempotencyKey: string = newIdempotencyKey(),
  options: StockRequestOptions = {},
): Promise<StagingTransferResult> {
  const result = fromCall(
    await callJson('POST', stagingTransfersPath(orderId), input, { 'Idempotency-Key': idempotencyKey }, options),
  );
  if (!('payload' in result)) {
    return result;
  }
  const row = parseStagingRow(result.payload);
  return row === null ? { status: 'invalid-response' } : { status: 'ready', row };
}
